/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Pressable, Radiobutton } from '@components/atoms'
import { Modal, ModalInfoType } from '@components/molecules'
import {
	Footer,
	PaymentItems,
	PaymentCustomerInfo,
	PaymentDelivery,
	PaymentCard,
	PaymentTotalCost,
	PaypalPayment,
	CardPaymentButton,
} from '@components/systems'
import { LinearProgress } from '@mui/material'

import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	ApiGetDataStatus,
	PaymentDeliveryType,
} from '@redux/index'
import { unsupportedPayPalCountries } from '@redux/store/home/payment.store'
import { useNavigate } from 'react-router-dom'
import { grey, deepPurple, pink, lime } from '@mui/material/colors'

const PaymentPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()

	// redux state
	const status: ApiGetDataStatus = useAppSelector(state => state.payment.paymentStatus)
	const showModal: boolean = useAppSelector(state => state.payment.showModal)
	const modalInfo: ModalInfoType = useAppSelector(state => state.payment.modalInfo)

	const paymentMethod: string = useAppSelector(state => state.payment.paymentMethod)
	const delivery: PaymentDeliveryType = useAppSelector(
		state => state.payment.paymentDelivery,
	)

	const isKrwPayment: boolean = React.useMemo(
		() => delivery.countryIsoCode === 'KR',
		[delivery],
	)

	const onModalSubmit = () => {}

	const onPaymentMethodClick = (paymentType: string) => {
		if (
			paymentType === 'paypal' &&
			unsupportedPayPalCountries.includes(delivery.countryIsoCode)
		) {
			reduxDispatch(
				paymentActions.MODAL_INFO({
					target: 'paypalNotAllowed',
					title: 'PayPal Unavailable',
				}),
			)
			reduxDispatch(paymentActions.SHOW_MODAL(true))
		} else {
			reduxDispatch(paymentActions.PAYMENT_METHOD(paymentType))
		}
	}

	return (
		<View css={style}>
			<View className="outerView">
				<View className="contentOuterView">
					<Text category="h1">PAYMENT</Text>
					<View className="contentView">
						{['init', 'loading'].includes(status) ? (
							<View className="loadingView">
								<LinearProgress className="loadingProgress" />
							</View>
						) : (
							<View className="paymentOuterView">
								<PaymentItems />

								<View className="paymentMethodOuterView">
									<View className="infoTitleView">
										<Text category="h3">Payment Methods</Text>
									</View>

									<View className="">
										<View className="paymentMethodView">
											<Pressable
												className="paymentMethodButton"
												onClick={() => onPaymentMethodClick('paypal')}
											>
												<Radiobutton value="paypal" selectedValue={paymentMethod} />
												<Text>PayPal</Text>
											</Pressable>
										</View>
										<View className="paymentMethodView">
											<Pressable
												className="paymentMethodButton"
												onClick={() => onPaymentMethodClick('credit')}
											>
												<Radiobutton value="credit" selectedValue={paymentMethod} />
												<Text>Credit/Debit Card</Text>
											</Pressable>
										</View>
									</View>
								</View>

								{paymentMethod !== '' && (
									<>
										<PaymentCustomerInfo />
										<PaymentDelivery />

										{!isKrwPayment && paymentMethod === 'credit' && <PaymentCard />}
										<PaymentTotalCost />

										{paymentMethod === 'paypal' && <PaypalPayment />}

										{paymentMethod === 'credit' && <CardPaymentButton />}
									</>
								)}

								{/* <View className="eventView">
									<View className="eventInnerView">
										<Text className="eventTitle" category="h1">
											📢 Pre-Order Closing Soon!
										</Text>
										<ul className="eventUl">
											<li className="eventLi">⏰ Finish in May</li>
											<li className="eventLi">🚀 Shipping starts in July</li>
											<li className="eventLi">🚛 Free shipping – no extra cost!</li>
										</ul>

										<Text className="eventPrice">💥 Special Prices:</Text>
										<p className="eventPriceUnit">
											Kedem 6: <span className="eventNormalPrice">$407</span> ⮕{' '}
											<span className="eventDiscountedPrice">$369</span>
										</p>
										<p className="eventPriceUnit">
											Kedem 4: <span className="eventNormalPrice">$341</span> ⮕{' '}
											<span className="eventDiscountedPrice">$305</span>
										</p>
									</View>
								</View> */}
							</View>
						)}
					</View>
				</View>
			</View>

			<Footer />

			<Modal
				target={modalInfo.target ?? ''}
				title={modalInfo.title}
				show={showModal}
				onCloseModal={() => {
					if (['serverError'].includes(modalInfo.target)) {
						navigate(-1)
					} else if (['paymentSuccess'].includes(modalInfo.target)) {
						navigate('/')
					}

					reduxDispatch(paymentActions.SHOW_MODAL(false))
				}}
				showSubmit={false}
				closeStatus="inherit"
				closeTitle={['serverError'].includes(modalInfo.target) ? 'Go Back' : 'Close'}
				submitTitle="Get Code"
				submitIconColor={deepPurple['A400']}
				isTitleDanger={[
					'serverError',
					'unexpectedError',
					'dataError',
					'invalidateDataError',
					'paymentError',
				].includes(modalInfo.target)}
				onSubmit={onModalSubmit}
			>
				{modalInfo.target === 'serverError' ? (
					<View css={{ width: '30rem' }}>
						<Text category="p2">An server error has occurred.</Text>
						<Text category="p2">The server is not responding.</Text>
						<Text category="p2">Please review your entries and try again.</Text>
					</View>
				) : modalInfo.target === 'unexpectedError' ? (
					<View css={{ width: '30rem' }}>
						<Text category="p2">An unexpected error has occurred.</Text>
						<Text category="p2">The requested data does not exist.</Text>
						<Text category="p2">Please review your entries and try again.</Text>
					</View>
				) : modalInfo.target === 'dataError' ? (
					<View css={{ width: '30rem' }}>
						<Text category="p2">An error occurred in the data.</Text>
						<Text category="p2">
							The database and the requested data are not the same.
						</Text>
						<Text category="p2">Please review your entries and try again.</Text>
					</View>
				) : modalInfo.target === 'paymentError' ? (
					<View css={{ width: '30rem' }}>
						<Text category="p2">There was an error processing the payment.</Text>
						<Text category="p2">Please check if any required fields are missing</Text>
						<Text category="p2">or incorrectly formatted.</Text>
						<Text category="p2">Review your information and try again.</Text>
					</View>
				) : modalInfo.target === 'paymentSuccess' ? (
					<View css={{ width: '30rem' }}>
						<Text category="p2">Your payment was successfully processed.</Text>
						<Text category="p2">Thank you for your purchase!</Text>
						<Text category="p2">A confirmation email has been sent to you.</Text>
					</View>
				) : modalInfo.target === 'paypalNotAllowed' ? (
					<View css={{ width: '30rem' }}>
						<Text category="p2">Sorry, PayPal is not available in your country.</Text>
						<Text category="p2">Please use a Credit or Debit card instead.</Text>
					</View>
				) : null}
			</Modal>
		</View>
	)
}

export default PaymentPage

const style = css({
	position: 'relative',
	width: '100%',

	'.outerView': {
		display: 'flex',
		paddingTop: '2rem',
		paddingBottom: '2rem',
		borderBottom: '1px solid',
		borderColor: grey[300],

		'.contentOuterView': {
			width: '100%',
			minHeight: 'calc(100vh - 10rem)',
			padding: '2rem',

			'.contentView': {
				borderTop: '1px solid',
				borderColor: grey[500],

				'.loadingView': {
					width: '20rem',
					margin: 'auto',
					paddingTop: '3rem',
				},

				'.paymentOuterView': {
					maxWidth: '60rem',
					margin: 'auto',

					'.paymentMethodOuterView': {
						paddingLeft: '1rem',
						textAlign: 'left',

						'.paymentMethodView': {
							paddingBottom: '0.5rem',

							'.paymentMethodButton': {
								width: '100%',
								display: 'flex',
								alignItems: 'center',
								border: '1px solid',
								borderColor: grey[300],
								borderRadius: '10px',
							},
						},
					},

					'.infoView': {
						marginBottom: '3rem',

						'.infoTitleView': {
							display: 'flex',
							justifyContent: 'flex-start',
							alignItems: 'center',
							paddingLeft: '1rem',
							width: '100%',
							height: '4rem',
						},

						'.infoInnerView': {
							padding: '1rem',

							'.infoSubtitleView': {
								marginBottom: '2rem',

								'.infoSubtitle': {
									textAlign: 'left',
									color: pink['A400'],
									margin: 0,
									marginBottom: '0.5rem',
								},
							},

							'.inputView': {
								width: '100%',
								marginBottom: '1.2rem',

								'&.left': {
									paddingRight: '0.5rem',
								},

								'&.right': {
									paddingLeft: '0.5rem',
								},

								'&.phonCountryCode': {
									width: '15rem',
									paddingRight: '0.5rem',
									marginLeft: '-0.3rem',
									marginTop: '-0.3rem',

									'.phonCountryCodeButton': {
										width: '100%',
									},
								},

								'&.phoneNumber': {
									paddingLeft: '0.5rem',
								},

								'.error': {
									borderColor: pink['A400'],
								},

								'.searchAddressButton': {
									width: '100%',
									padding: 0,
								},
							},

							'.inputOuterView': {
								display: 'flex',
								alignItems: 'flex-start',
								justifyContent: 'space-between',
							},
						},
					},

					'.eventView': {
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: '100%',
						marginTop: '2rem',
						padding: '2rem 4rem',
						background: lime['A200'],
						borderRadius: '10px',

						'.eventInnerView': {
							width: '24rem',
							color: grey[900],
							textAlign: 'left',

							'.eventTitle': {
								fontSize: '30px',
								marginTop: '0',
								marginBottom: '0',
							},

							'.eventUl': {
								'.eventLi': {
									fontSize: '20px',
									fontWeight: '800',
									marginLeft: '1rem',
									marginBottom: '0.5rem',
								},
							},

							'.eventPrice': {
								fontSize: '30px',
								marginTop: '2rem',
								marginBottom: '1rem',
							},

							'.eventPriceUnit': {
								marginLeft: '3rem',
								marginTop: '0',
								marginBottom: '0',
								fontSize: '20px',
								fontWeight: '800',

								'.eventNormalPrice': {
									marginLeft: '1rem',
									marginRight: '1rem',
									textDecoration: 'line-through',
									fontSize: '15px',
									fontWeight: '300',
								},
								'.eventDiscountedPrice': {
									marginLeft: '1rem',
									fontSize: '30px',
								},
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 600px)': {
		'.outerView': {
			'.contentOuterView': {
				'.contentView': {
					'.paymentOuterView': {
						'.infoView': {
							'.infoInnerView': {
								'.inputView': {
									'&.left': {
										paddingRight: 0,
									},

									'&.right': {
										paddingLeft: 0,
									},

									'&.phonCountryCode': {
										width: 'calc(100% + 0.5rem)',
										paddingRight: 0,
									},

									'&.phoneNumber': {
										paddingLeft: 0,
									},
								},
								'.inputOuterView': {
									display: 'block',
								},
							},
						},

						'.eventView': {
							padding: '1rem',
							'.eventInnerView': {
								width: '30rem',
								padding: '0',

								'.eventTitle': {
									fontSize: '20px',
								},

								'.eventUl': {
									'.eventLi': {
										marginLeft: '0rem',
										fontSize: '15px',
									},
								},

								'.eventPrice': {
									fontSize: '20px',
								},

								'.eventPriceUnit': {
									marginLeft: '2rem',
									fontSize: '15px',

									'.eventNormalPrice': {
										marginLeft: '1rem',
										marginRight: '1rem',
										fontSize: '12px',
									},
									'.eventDiscountedPrice': {
										fontSize: '15px',
									},
								},
							},
						},
					},
				},
			},
		},
	},
})
